import DataTable from 'Components/DataTable';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import { isNumber, partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useAppSelector as useSelector } from 'Store/index';
import { useMyServiceContext } from '..';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { DateTime } from 'luxon';
import { partialDateFromStartInclusive, TASK_DATE_FORMAT } from 'Utility/tasklist';

const parseDate = (value: string | null | undefined): DateTime | null => {
  if (!value) return null;
  const dt = DateTime.fromFormat(value, TASK_DATE_FORMAT);
  return dt.isValid ? dt : null;
};

const compareDates = (a: string | null | undefined, b: string | null | undefined): number => {
  const dtA = parseDate(a);
  const dtB = parseDate(b);

  if (dtA === null && dtB === null) return 0;
  if (dtA === null) return 1;
  if (dtB === null) return -1;

  return dtA.toMillis() - dtB.toMillis();
};

const InvitesHistory = ({ invites, inviteErrors, promTasks }: IOwnProps): React.JSX.Element => {
  const { fm, setEditingObj } = useMyServiceContext<'_invite'>();

  const platform = useSelector(s => s.session.platforms?.selected);
  const orgs = useSelector(s => s.session.orgGroupArray);
  const orgTaskUISettings = useSelector(s => s.settings.orgSettings.taskUiSettings);
  const patientContactInfo = useSelector(s => s.patient.contactInfo);

  const invitesData: Array<[React.JSX.Element, string, string, string]> = invites
    .toSorted((a, b) => b.timestamp - a.timestamp)
    .filter(i => isNumber(i.timestamp))
    .map(i => [
      <div key={`date-${i.id}`} style={{ whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>
        {formatPartialDate(partialDateFromDate(new Date(i.timestamp)))}
      </div>,
      fm(`myService.opts.${i.medium === 'EMAIL' ? 'email' : 'sms'}`),
      i.carrier ?? '',
      i.org_id ? (orgs?.find(o => o.orgId === i.org_id)?.displayName ?? i.org_id) : '',
    ]);

  /** TODO Miten tää toimii, pitääkö kutsu pystyä lähettämään vaikka kutsu olisikin jo lähetetty herätteen ohessa */
  /** TODO Pitäskö tekstin olla "Kutsu lähetettiin _ensimmäisen_ herätteen lähetyspäivämäärällä 2023-08-10", onko päivämäärä (start_inclusive) oikein? */
  /** TODO Tooltip sais kai kertoo syyn, miksei voi lähettää uutta kutsua */
  const inviteSentTogetherWithFirstTasklist = invites.length === 0 && promTasks.length > 0;

  const firstTasklistStartInclusive = promTasks
    .toSorted((a, b) => compareDates(a.start_inclusive, b.start_inclusive))
    .at(0)?.start_inclusive;

  const hasTaskUISettingsForPlatform = !!(platform && orgTaskUISettings?.[platform]);

  return (
    <>
      <Container style={{ justifyContent: 'space-between' }}>
        <Item>
          <HistoryRowSubHeader header={fm('myService.invites')} />
        </Item>
        <Item>
          <ActionButtonRounded
            text="myService.newInvite"
            filled={true}
            uppercase={false}
            width={9.6}
            height={3}
            fontSize={16}
            onClick={() => setEditingObj({ type: '_invite', data: {} })}
            disabled={
              !hasTaskUISettingsForPlatform ||
              inviteSentTogetherWithFirstTasklist ||
              !!patientContactInfo?.contactNotAllowed
            }
            disabledTooltip={
              !hasTaskUISettingsForPlatform ? (
                <span>{fm('myService.noTasklistSettingsDefined')}</span>
              ) : inviteSentTogetherWithFirstTasklist ? undefined : (
                <span>{fm('myService.newInviteDisabledTooltip')}</span>
              )
            }
          />
        </Item>
      </Container>
      {inviteSentTogetherWithFirstTasklist && firstTasklistStartInclusive ? (
        fm(
          `myService.firstInviteUpcomingInfo.${DateTime.fromFormat(firstTasklistStartInclusive, TASK_DATE_FORMAT) < DateTime.now() ? 'past' : 'present'}`,
          {
            date: formatPartialDate(partialDateFromStartInclusive(firstTasklistStartInclusive)),
            strong: chunks => (
              <span key={`${chunks}`} style={{ fontWeight: '600' }}>
                {React.Children.toArray(chunks)}
              </span>
            ),
          },
        )
      ) : (
        <DataTable
          headers={['sendingDate', 'sendingMedium', 'numberOrAddress', 'sendingOrg']}
          headersFormatter={h => h && fm(`myService.${h}`)}
          data={{ rowData: invitesData }}
        />
      )}
      <div style={{ color: 'red', marginTop: '2rem' }}>
        {inviteErrors.map(
          err =>
            err.statusCode === 429 && (
              <div key={err.method}>
                {fm('errors.invites.' + err.method)}
                {' - '}
                {fm('errors.invites.' + err.statusCode)}
              </div>
            ),
        )}
      </div>
    </>
  );
};

InvitesHistory.displayName = 'InvitesHistory';

interface IOwnProps {
  invites: Array<INeuroStimulus>;
  inviteErrors: Array<{ method: ContactInfoType; statusCode: number }>;
  promTasks: Array<ITaskList>;
}

export default InvitesHistory;
